import { CreatePoiPayload, NodePoint } from '../types/types';
import enkarturPois from '../../data/enkartur/poi_enkartur.json';

export interface PoiResponse<T> {
  data: T;
  message: string;
}

export interface PoiData {
  id: string;
  tenant_id: string;
  title: string;
  lat: number;
  lon: number;
  short_description: string | null;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

type EnkarturPoiRecord = {
  id_content?: number;
  title?: string;
  [key: string]: unknown;
};

const stripHtml = (value: string): string => {
  const normalized = value.replace(/&nbsp;/gi, ' ');

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(normalized, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  return normalized
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const resolveBaseUrl = (): string => {
  let envValue: string | undefined;

  if (typeof import.meta !== 'undefined') {
    const meta = import.meta as { env?: Record<string, string | undefined> };
    if (meta.env) {
      envValue = meta.env.VITE_API_BACKEND_BASE_URL ?? meta.env.REACT_APP_API_BACKEND_BASE_URL;
    }
  }

  const baseUrl = envValue ?? 'http://localhost:8080';
  return baseUrl.replace(/\/$/, '');
};

const apiBaseUrl = resolveBaseUrl();

const buildPoiUrl = (tenantId: string) => `${apiBaseUrl}/api/tenant/${tenantId}/pois`;

async function createPoi(tenantId: string, payload: CreatePoiPayload): Promise<PoiResponse<PoiData>> {
  const response = await fetch(buildPoiUrl(tenantId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => ({ data: null, message: 'Unexpected response' }));

  if (!response.ok) {
    const errorMessage = typeof responseBody?.message === 'string' ? responseBody.message : 'Failed to create POI';
    throw new Error(errorMessage);
  }

  return responseBody as PoiResponse<PoiData>;
}

async function getTenantPois(tenantId: string): Promise<NodePoint[]> {
  if (tenantId !== 'enkartur') {
    return [];
  }

  return (enkarturPois as EnkarturPoiRecord[])
    .filter((poi) => {
      const lat = poi['coordenada X'];
      const lon = poi['coordenada Y'];
      return typeof poi.title === 'string' && typeof lat === 'number' && typeof lon === 'number';
    })
    .map((poi) => ({
      id: String(poi.id_content ?? poi.title),
      lat: poi['coordenada X'] as number,
      lon: poi['coordenada Y'] as number,
      title: poi.title as string,
      description:
        typeof poi['descripcion corta'] === 'string' && poi['descripcion corta'].trim().length > 0
          ? stripHtml(poi['descripcion corta'])
          : undefined,
    }));
}

export const poiService = {
  createPoi,
  getTenantPois,
};
